import React from 'react';
import { BrowserRouter, Routes, Route, Navigate, useParams, useLocation } from 'react-router-dom';
import { ThemeProvider, createTheme, CssBaseline } from '@mui/material';
import { AuthProvider, useAuth } from './contexts/AuthContext';
import BookingPage from './pages/public/BookingPage';
import MyBookingsPage from './pages/public/MyBookingsPage';
import PublicCompanyPage from './pages/PublicCompanyPage';
import PaymentWall from './components/PaymentWall';
import TrialExpiredWall from './components/TrialExpiredWall';

import TrackingScripts from './components/TrackingScripts';
import ErrorBoundary from './components/ErrorBoundary';

// Telas internas/protegidas carregadas sob demanda (code splitting)
const LoginPage = React.lazy(() => import('./pages/LoginPage'));
const DashboardPage = React.lazy(() => import('./pages/dashboard/DashboardPage'));
const ServicesPage = React.lazy(() => import('./pages/services/ServicesPage'));
const ProfessionalsPage = React.lazy(() => import('./pages/professionals/ProfessionalsPage'));
const AgendaPage = React.lazy(() => import('./pages/agenda/AgendaPage'));
const RevenuePage = React.lazy(() => import('./pages/revenue/RevenuePage'));
// Prévia da Visão Geral financeira (visual + recebido/diferenças). Seleção de
// versão só de apresentação, pela empresa da sessão autenticada — não
// substitui isolamento nem autorização do backend, que continuam iguais para
// as duas versões (mesmos endpoints, mesmos dados, mesmo escopo por empresa).
const RevenuePageV2 = React.lazy(() => import('./pages/revenue/RevenuePageV2'));
const CustomersPage = React.lazy(() => import('./pages/customers/CustomersPage'));
const SettingsPage = React.lazy(() => import('./pages/settings/SettingsPage'));
const ProductsPage = React.lazy(() => import('./pages/products/ProductsPage'));
const SubscriptionsPage = React.lazy(() => import('./pages/subscriptions/SubscriptionsPage'));
const ReviewsPage = React.lazy(() => import('./pages/reviews/ReviewsPage'));
const NotificationsPage = React.lazy(() => import('./pages/notifications/NotificationsPage'));
const IntegrationsPage = React.lazy(() => import('./pages/integrations/IntegrationsPage'));
const SignupPage = React.lazy(() => import('./pages/SignupPage'));
// Cadastro V2 (piloto). O gate real e o backend: com a flag desligada as
// rotas de API respondem 404 e as telas mostram 'pagina nao disponivel'.
const SignupV2Page = React.lazy(() => import('./pages/SignupV2Page'));
const OnboardingPage = React.lazy(() => import('./pages/OnboardingPage'));
const ForgotPasswordPage = React.lazy(() => import('./pages/ForgotPasswordPage'));
const ResetPasswordPage = React.lazy(() => import('./pages/ResetPasswordPage'));
const ConfirmEmailChangePage = React.lazy(() => import('./pages/ConfirmEmailChangePage'));
const AdminPanel = React.lazy(() => import('./pages/AdminPanel'));
const BloquearHorariosPage = React.lazy(() => import('./pages/blocked/BloquearHorariosPage'));
const LogsPage = React.lazy(() => import('./pages/logs/LogsPage'));
const MeuPlanoPage = React.lazy(() => import('./pages/plano/MeuPlanoPage'));
const AppHelpPage = React.lazy(() => import('./pages/AppHelpPage'));
const ComandasPage = React.lazy(() => import('./pages/comandas/ComandasPage'));
const GrowthPage = React.lazy(() => import('./pages/growth/GrowthPage'));
const ParceirosPage = React.lazy(() => import('./pages/parceiros/ParceirosPage'));
const ConversationFlowsPage = React.lazy(() => import('./pages/conversationFlows/ConversationFlowsPage'));
const ReportsPage = React.lazy(() => import('./pages/reports/ReportsPage'));
const ReportProfessionalsPage = React.lazy(() => import('./pages/reports/ReportProfessionalsPage'));
const ReportProfessionalDetailPage = React.lazy(() => import('./pages/reports/ReportProfessionalDetailPage'));
const ReportCustomersPage = React.lazy(() => import('./pages/reports/ReportCustomersPage'));
const ReportPackagesPage = React.lazy(() => import('./pages/reports/ReportPackagesPage'));
const ReportSumidosPage = React.lazy(() => import('./pages/reports/ReportSumidosPage'));
const ReportReviewsPage = React.lazy(() => import('./pages/reports/ReportReviewsPage'));
const ReportProductsPage = React.lazy(() => import('./pages/reports/ReportProductsPage'));
const WaitlistPage = React.lazy(() => import('./pages/waitlist/WaitlistPage'));
const CashflowPage = React.lazy(() => import('./pages/cashflow/CashflowPage'));
const FeedbacksPage = React.lazy(() => import('./pages/feedbacks/FeedbacksPage'));
const ExpensesPage = React.lazy(() => import('./pages/expenses/ExpensesPage'));
const ConfirmarVagaPage = React.lazy(() => import('./pages/waitlist/ConfirmarVagaPage'));

const ScrollToTop = () => {
  const { pathname } = useLocation();
  React.useEffect(() => { window.scrollTo(0, 0); }, [pathname]);
  return null;
};

const theme = createTheme({
  palette: {
    primary: { main: '#1976d2' },
    secondary: { main: '#c9a227' },
    background: { default: '#f5f5f7' },
  },
  typography: { fontFamily: '"Inter", "Roboto", sans-serif' },
  components: {
    MuiCard: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiPaper: { styleOverrides: { root: { borderRadius: 12 } } },
    MuiButton: { styleOverrides: { root: { borderRadius: 8, textTransform: 'none', fontWeight: 600 } } },
  },
});

const ProtectedRoute = ({ children }: { children: React.ReactElement }) => {
  const { user, loading } = useAuth();
  const location = useLocation();
  if (loading) return null;
  if (!user) return <Navigate to="/login" />;

  // Cadastro V2: empresa-rascunho volta para o onboarding de onde parou.
  // Empresa legada (sem a chave) e empresa publicada seguem o fluxo normal.
  if ((user as any).company?.onboarding?.status === 'draft' && location.pathname !== '/onboarding') {
    return <Navigate to="/onboarding" replace />;
  }

  if (user.role !== 'superadmin') {
    // Trial expirado: mostra wall de assinatura (cobre tudo)
    if (user.payment_status?.trial_expired && !location.pathname.startsWith('/plano')) {
      return <Navigate to="/plano" replace />;
    }
    // Pagamento atrasado > 5 dias: mostra PaymentWall
    if (user.payment_status?.is_blocked) {
      return <PaymentWall />;
    }
  }
  return children;
};

// Visão Geral nova liberada para todas as empresas em 07/09/2026 (piloto da
// empresa 7 validado ao vivo antes desta liberação). Ponto único de reversão:
// para voltar ao piloto, trocar o corpo por
//   const { user } = useAuth();
//   return (user as any)?.company_id === 7 ? <RevenuePageV2 /> : <RevenuePage />;
const RevenueRouteGate = () => {
  return <RevenuePageV2 />;
};

const RootRedirect = () => {
  const { user, loading } = useAuth();
  if (loading) return null;
  return user ? <Navigate to="/dashboard" replace /> : <Navigate to="/login" replace />;
};

const BookingWrapper = () => {
  const { slug } = useParams<{ slug: string }>();
  return <BookingPage slug={slug || ''} />;
};

// Detecta domínio customizado (não é agendali.com.br nem app.agendali.com.br)
const KNOWN_HOSTS = ['agendali.com.br', 'app.agendali.com.br', 'www.agendali.com.br', 'localhost'];
function checkIsCustomDomain() {
  const hostname = window.location.hostname.toLowerCase().split(':')[0];
  // Subdomínios "app." são sempre painel admin (ex: app.ouropagocg.com.br)
  if (hostname.startsWith('app.')) return false;
  return !KNOWN_HOSTS.some(h => hostname === h || hostname.endsWith('.' + h));
}

const CustomDomainRouter = () => {
  const [slug, setSlug] = React.useState<string | null>(null);
  const [notFound, setNotFound] = React.useState(false);
  React.useEffect(() => {
    const domain = window.location.hostname;
    fetch(`/api/resolve-domain?domain=${encodeURIComponent(domain)}`)
      .then(r => r.ok ? r.json() : Promise.reject())
      .then(data => setSlug(data.slug))
      .catch(() => setNotFound(true));
  }, []);
  if (notFound) return <div style={{ display:'flex',alignItems:'center',justifyContent:'center',height:'100vh',fontFamily:'sans-serif',color:'#888' }}>Domínio não configurado.</div>;
  if (!slug) return null;
  return (
    <BrowserRouter>
      <ScrollToTop />
      <TrackingScripts />
      <ErrorBoundary>
      <React.Suspense fallback={null}>
      <Routes>
        <Route path="/meus-agendamentos" element={<MyBookingsPage />} />
        <Route path="*" element={<BookingPage slug={slug} />} />
      </Routes>
      </React.Suspense>
      </ErrorBoundary>
    </BrowserRouter>
  );
};

// Gate que bloqueia qualquer rota quando trial expirou ou pagamento bloqueado
function AppGate({ children }: { children: React.ReactElement }) {
  const { user, loading } = useAuth();
  const location = useLocation();
  // Rotas de reset de senha nunca devem ser bloqueadas
  const PUBLIC_PATHS = ['/forgot-password', '/reset-password', '/confirmar-email', '/login', '/signup', '/plano'];
  if (PUBLIC_PATHS.some(p => location.pathname.startsWith(p))) return children;
  if (loading) return null;
  if (!user || user.role === 'superadmin') return children;
  if (user.payment_status?.trial_expired) return <Navigate to="/plano" replace />;
  if (user.payment_status?.is_blocked) return <PaymentWall />;
  return children;
}


// Fallback leve exibido durante o carregamento sob demanda de telas internas
function PageLoadingFallback() {
  return (
    <div style={{ display:'flex', alignItems:'center', justifyContent:'center', height:'100vh' }}>
      <div style={{ width:32, height:32, border:'3px solid #e0e0e0', borderTopColor:'#1976d2', borderRadius:'50%', animation:'spin 0.8s linear infinite' }} />
      <style>{'@keyframes spin { to { transform: rotate(360deg); } }'}</style>
    </div>
  );
}

function App() {
  const isCustomDomain = checkIsCustomDomain();
  if (isCustomDomain) {
    return (
      <ThemeProvider theme={theme}>
        <CssBaseline />
        <CustomDomainRouter />
      </ThemeProvider>
    );
  }

  return (
    <ThemeProvider theme={theme}>
      <CssBaseline />
      <AuthProvider>
        <BrowserRouter>
          <ScrollToTop />
          <TrackingScripts />
          <AppGate>
          <ErrorBoundary>
          <React.Suspense fallback={<PageLoadingFallback />}>
          <Routes>
            {/* Landing & Public Routes */}
            <Route path="/" element={<RootRedirect />} />
            {/* Cadastro oficial: /signup renderiza o Signup V2 diretamente (a URL
                visível continua /signup, sem redirect). /signup-v2 segue como
                alias temporário do mesmo componente. SignupPage (V1) permanece
                importado e intacto — sem nenhum link público apontando para
                ele — só para rollback de 1 linha, se necessário. */}
            <Route path="/signup" element={<SignupV2Page />} />
            <Route path="/signup-v2" element={<SignupV2Page />} />
            <Route path="/register" element={<SignupPage />} />
            <Route path="/login" element={<LoginPage />} />
            <Route path="/forgot-password" element={<ForgotPasswordPage />} />
            <Route path="/reset-password" element={<ResetPasswordPage />} />
            <Route path="/confirmar-email" element={<ConfirmEmailChangePage />} />
            <Route path="/book/:slug" element={<BookingWrapper />} />
            <Route path="/app/:slug" element={<PublicCompanyPage />} />
            <Route path="/meus-agendamentos" element={<MyBookingsPage />} />

            {/* Admin Routes */}
            <Route path="/admin" element={<ProtectedRoute><AdminPanel /></ProtectedRoute>} />

            {/* Protected Dashboard Routes */}
            <Route path="/onboarding" element={<ProtectedRoute><OnboardingPage /></ProtectedRoute>} />
            <Route path="/dashboard" element={<ProtectedRoute><DashboardPage /></ProtectedRoute>} />
            <Route path="/agenda" element={<ProtectedRoute><AgendaPage /></ProtectedRoute>} />
            <Route path="/services" element={<ProtectedRoute><ServicesPage /></ProtectedRoute>} />
            <Route path="/professionals" element={<ProtectedRoute><ProfessionalsPage /></ProtectedRoute>} />
            <Route path="/customers" element={<ProtectedRoute><CustomersPage /></ProtectedRoute>} />
            <Route path="/revenue" element={<ProtectedRoute><RevenueRouteGate /></ProtectedRoute>} />
            <Route path="/cashflow" element={<ProtectedRoute><CashflowPage /></ProtectedRoute>} />
            <Route path="/expenses" element={<ProtectedRoute><ExpensesPage /></ProtectedRoute>} />
            <Route path="/settings" element={<ProtectedRoute><SettingsPage /></ProtectedRoute>} />
            <Route path="/app-help" element={<ProtectedRoute><AppHelpPage /></ProtectedRoute>} />
            <Route path="/products" element={<ProtectedRoute><ProductsPage /></ProtectedRoute>} />
            <Route path="/subscriptions" element={<ProtectedRoute><SubscriptionsPage /></ProtectedRoute>} />
            <Route path="/reviews" element={<ProtectedRoute><ReviewsPage /></ProtectedRoute>} />
            <Route path="/notificacoes" element={<ProtectedRoute><NotificationsPage /></ProtectedRoute>} />
            <Route path="/integrations" element={<ProtectedRoute><IntegrationsPage /></ProtectedRoute>} />

            {/* Novas rotas */}
            <Route path="/blocked-hours" element={<ProtectedRoute><BloquearHorariosPage /></ProtectedRoute>} />
            <Route path="/logs" element={<ProtectedRoute><LogsPage /></ProtectedRoute>} />
            <Route path="/feedbacks" element={<ProtectedRoute><FeedbacksPage /></ProtectedRoute>} />
            <Route path="/plano" element={<ProtectedRoute><MeuPlanoPage /></ProtectedRoute>} />
            <Route path="/comandas" element={<ProtectedRoute><ComandasPage /></ProtectedRoute>} />
            <Route path="/crescimento" element={<ProtectedRoute><GrowthPage /></ProtectedRoute>} />
            <Route path="/parceiros" element={<ProtectedRoute><ParceirosPage /></ProtectedRoute>} />
            <Route path="/fluxos-conversa" element={<ProtectedRoute><ConversationFlowsPage /></ProtectedRoute>} />
            <Route path="/waitlist" element={<ProtectedRoute><WaitlistPage /></ProtectedRoute>} />
            <Route path="/reports" element={<ProtectedRoute><ReportsPage /></ProtectedRoute>} />
            <Route path="/reports/professionals" element={<ProtectedRoute><ReportProfessionalsPage /></ProtectedRoute>} />
            <Route path="/reports/professionals/:id" element={<ProtectedRoute><ReportProfessionalDetailPage /></ProtectedRoute>} />
            <Route path="/reports/customers" element={<ProtectedRoute><ReportCustomersPage /></ProtectedRoute>} />
            <Route path="/reports/packages" element={<ProtectedRoute><ReportPackagesPage /></ProtectedRoute>} />
            <Route path="/reports/sumidos" element={<ProtectedRoute><ReportSumidosPage /></ProtectedRoute>} />
            <Route path="/reports/reviews" element={<ProtectedRoute><ReportReviewsPage /></ProtectedRoute>} />
            <Route path="/reports/products" element={<ProtectedRoute><ReportProductsPage /></ProtectedRoute>} />
            <Route path="/confirmar/:token" element={<ConfirmarVagaPage />} />

            {/* Rota curta: agendali.com.br/lua → página de agendamento */}
            <Route path="/:slug" element={<BookingWrapper />} />
          </Routes>
          </React.Suspense>
          </ErrorBoundary>
          </AppGate>
        </BrowserRouter>
      </AuthProvider>
    </ThemeProvider>
  );
}

export default App;
